import React, { useCallback, useEffect, useRef, useState } from 'react';

import c from 'classnames';
import { Search } from 'lucide-react';

import { type QueryParams, loadQuery } from 'common/actions/ideaQueries';
import connect from 'common/core/connect';
import TextInput from 'common/inputs/TextInput';
import { KeyNames } from 'common/KeyCodes';
import SpinnerV2 from 'common/SpinnerV2';
import { getIdeaQuery } from 'common/subdomain/admin/v2/AdminV2IdeasTableV2/utils';
import IdeaSearchAncestors from 'common/subdomain/admin/v2/ideas/IdeaSearchAncestors';
import { P, Span } from 'common/ui/Text';
import useDelayer from 'common/util/useDelayer';
import styles from 'css-module/components/subdomain/admin/v2/AdminV2IdeaDrawer/_IdeaSearch.module.scss';

import type { Idea } from 'common/api/endpoints/idea';
import type { IdeaIDQueriesState, IdeaQueryState } from 'common/reducers/ideaQueries';
import type { IdeasState } from 'common/reducers/ideas';

type ConnectProps = {
  loadQuery: (queryParams: QueryParams) => Promise<void>;
};
type OwnProps = {
  onSelectIdea: (idea: Idea) => void;
  filterResults?: (ideas: Idea[]) => Idea[];
};

type Props = OwnProps & ConnectProps;

function IdeaSearch({ filterResults, loadQuery, onSelectIdea }: Props) {
  const [search, setSearch] = useState('');

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setSearch(value);

    if (value) {
      loadQuery({ search: value });
    }
  };

  return (
    <div className={styles.ideaSearch}>
      <TextInput
        autoFocus
        className={styles.ideaSearchInput}
        onChange={useDelayer(onSearchChange, 250)}
        prefix={<Search className={styles.ideaSearchIcon} size={16} />}
        placeholder="Search ideas"
      />
      {search && (
        <div className={styles.ideaList}>
          <IdeaSearchResults
            filterResults={filterResults}
            onSelectIdea={onSelectIdea}
            search={search}
          />
        </div>
      )}
    </div>
  );
}

export default connect(null, {
  loadQuery,
})(IdeaSearch) as unknown as React.FC<OwnProps>;

type IdeaSearchResultsConnectProps = {
  searchQuery?: IdeaQueryState;
};
type IdeaSearchResultsOwnProps = {
  filterResults?: (ideas: Idea[]) => Idea[];
  onSelectIdea: (idea: Idea) => void;
  search: string;
};

type IdeaSearchResultsProps = IdeaSearchResultsOwnProps & IdeaSearchResultsConnectProps;

const UnconnectedIdeaSearchResults = ({
  filterResults,
  onSelectIdea,
  search,
  searchQuery,
}: IdeaSearchResultsProps) => {
  const ideaItemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const unfilteredIdeas = searchQuery?.items ?? [];
  const ideas = filterResults ? filterResults(unfilteredIdeas) : unfilteredIdeas;

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === KeyNames.UpArrow) {
        const currentIndex = hoveredIndex ?? -1;
        const nextIndex = (currentIndex - 1 + ideas.length) % ideas.length;
        setHoveredIndex(nextIndex);
      } else if (event.key === KeyNames.DownArrow) {
        const currentIndex = hoveredIndex ?? -1;
        const nextIndex = (currentIndex + 1) % ideas.length;
        setHoveredIndex(nextIndex);
      } else if (event.key === KeyNames.Enter) {
        if (hoveredIndex) {
          const idea = ideas[hoveredIndex];
          onSelectIdea(idea);
        }
      }
    },
    [hoveredIndex, ideas, onSelectIdea],
  );

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  useEffect(() => {
    if (hoveredIndex !== null && ideaItemRefs.current[hoveredIndex]) {
      const hoveredItem = ideaItemRefs.current[hoveredIndex];
      if (hoveredItem) {
        hoveredItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [hoveredIndex]);

  const isSearching = search && search.trim();
  if (!isSearching) {
    return null;
  }

  const isLoading = !searchQuery || searchQuery.loading;
  if (isLoading) {
    return <SpinnerV2 className={styles.noIdeas} size="small" />;
  }

  const isError = !searchQuery.items && searchQuery.error;
  if (isError) {
    return (
      <P className={styles.noIdeas} variant="bodyMd">
        Something went wrong
      </P>
    );
  }

  const noResults = ideas.length === 0;
  if (noResults) {
    return (
      <P className={styles.noIdeas} variant="bodyMd">
        No ideas found
      </P>
    );
  }

  return (
    <>
      {ideas.map((idea, index) => {
        const { title } = idea;
        const isHovered = hoveredIndex === index;

        return (
          <button
            className={c(styles.ideaItem, { [styles.ideaItemHovered]: isHovered })}
            key={idea._id}
            onClick={() => onSelectIdea(idea)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            ref={(el) => (ideaItemRefs.current[index] = el)}>
            <IdeaSearchAncestors idea={idea} />
            <Span className={styles.ideaName} variant="bodyMd">
              {title}
            </Span>
          </button>
        );
      })}
    </>
  );
};

const IdeaSearchResults = connect(
  (
    { ideas, ideaQueries }: { ideas: IdeasState; ideaQueries: IdeaIDQueriesState },
    { search }: IdeaSearchResultsOwnProps,
  ): IdeaSearchResultsConnectProps => {
    if (!search || !search.trim()) {
      return {};
    }

    return {
      searchQuery: getIdeaQuery({
        ideas,
        ideaQueries,
        queryParams: { search },
      }),
    };
  },
)(UnconnectedIdeaSearchResults) as unknown as React.FC<IdeaSearchResultsOwnProps>;
